import type { PublicationNoticeModel, PublicationStateRow } from "@/lib/listing-publication-gate";

const toneText: Record<PublicationStateRow["tone"], string> = {
  ok: "text-emerald-700",
  warn: "text-amber-700",
  bad: "text-red-700",
  neutral: "text-slate-600",
};

const toneChip: Record<PublicationStateRow["tone"], string> = {
  ok: "bg-emerald-50 border-emerald-100",
  warn: "bg-amber-50 border-amber-100",
  bad: "bg-red-50 border-red-100",
  neutral: "bg-slate-50 border-slate-200",
};

/**
 * Read-only presentation of the five independent listing states. A READY
 * readiness result is shown as required-but-not-sufficient, and the specific
 * reason publication is blocked is always named.
 */
export default function PublicationGateNotice({
  model,
  variant = "compact",
}: {
  model: PublicationNoticeModel;
  variant?: "compact" | "full";
}) {
  const { gate, states } = model;

  if (variant === "compact") {
    if (gate.allowed) return null;
    return (
      <div
        data-testid="publication-gate-notice"
        className={`px-3 py-2 rounded-lg border text-xs ${toneChip[gate.tone]}`}
      >
        <p className={`font-medium ${toneText[gate.tone]}`}>
          <i className="ri-lock-line mr-1" />
          {gate.label}
        </p>
        <p className="text-slate-600 mt-0.5">{gate.message}</p>
        {gate.code === "reconciliation_required" && (
          <p className="text-slate-500 mt-0.5">
            Readiness and approval do not override an unresolved remote outcome.
          </p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="publication-state-panel" className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Publication state</h3>
        <span className={`text-xs font-medium px-2 py-1 rounded-lg border ${toneChip[gate.tone]} ${toneText[gate.tone]}`}>
          {gate.allowed ? "Publish permitted" : "Publish blocked"}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {states.map((row) => (
          <div key={row.key} className="border border-slate-100 rounded-lg p-2.5 min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">{row.label}</p>
            <p className={`text-sm font-medium mt-0.5 ${toneText[row.tone]}`}>{row.value}</p>
            {row.note && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{row.note}</p>}
          </div>
        ))}
      </div>
      {!gate.allowed && (
        <p className="text-xs text-slate-600 mt-3" data-testid="publication-block-reason">
          <i className="ri-information-line mr-1" />
          {gate.message}
        </p>
      )}
    </div>
  );
}